<template>
  <v-dialog v-model="showDialog" width="1000">
    <v-card class="pa-3" v-if="form">
      <validation-observer tag="form" ref="form">
        <v-card-title class="headline" primary-title>
          <span v-if="form.title">{{ form.title }}</span>
          <span v-else>New resource video</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <validation-provider
                rules="required|max:255|min:3"
                name="title"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="form.title"
                  outlined
                  required
                  label="Title"
                  :error-messages="errors"
                >
                </v-text-field>
              </validation-provider>

              <validation-provider
                rules="required|min:50"
                name="description"
                v-slot="{ errors }"
              >
                <v-textarea
                  v-model="form.description"
                  outlined
                  label="Description"
                  :error-messages="errors"
                >
                </v-textarea>
              </validation-provider>

              <validation-provider
                rules="required"
                name="notes"
                v-slot="{ errors }"
              >
                <quill-editor
                  :key="'notes-' + quillKey"
                  :error-messages="errors"
                  placeholder="Write notes"
                  v-model="form.notes"
                />
              </validation-provider>

              <validation-provider
                rules="required"
                name="tips"
                v-slot="{ errors }"
              >
                <quill-editor
                  :key="'tips-' + quillKey"
                  :error-messages="errors"
                  placeholder="Write tips"
                  v-model="form.tips"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                rules="required"
                name="video url"
                vid="video_url"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="form.video_url"
                  required
                  outlined
                  label="Video url"
                  :error-messages="errors"
                >
                </v-text-field>
              </validation-provider>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                name="thumbnail image"
              >
                <input hidden :value="videoCover" />
                <v-img
                  class="mb-4"
                  v-if="videoCover"
                  :src="videoCover"
                  :lazy-src="videoCover"
                  size="100%"
                />
                <v-btn @click="addCover" color="primary">
                  {{ videoCover ? 'Change' : 'Add' }} thumbnail image
                </v-btn>
                <div
                  v-if="errors.length"
                  class="v-messages theme--light error--text mt-3 pl-3"
                >
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ errors[0] }}
                    </div>
                  </div>
                </div>
              </validation-provider>

              <div v-if="form.id" class="mt-6 title">
                Attachments
                <span v-if="form.attachments.length">
                  ({{ form.attachments.length }})
                </span>
                <v-btn @click="addAttachment" :icon="true" color="primary">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>

                <input
                  type="file"
                  ref="attachmentPicker"
                  @change="onAttachmentAdded"
                  v-show="false"
                  accept=".pdf,.doc,.docx"
                />

                <div class="mt-2" v-if="form.attachments.length">
                  <div
                    class="mb-2"
                    v-for="(attachment, index) in form.attachments"
                    :key="attachment.id"
                  >
                    <v-chip
                      @click="viewAttachment(attachment)"
                      @click:close="removeAttachment(attachment, index)"
                      close
                    >
                      <!-- {{ attachment.name }} -->
                      Attachment
                    </v-chip>
                  </div>
                </div>
                <div v-else class="subtitle-1 grey--text">
                  No attachments added
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Image cropper component -->
          <image-cropper
            v-model="showImageUpload"
            :aspect-ratio="16 / 9"
            @change="imageChanged"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            :disabled="loading"
            :loading="loading"
            color="primary"
            text
            @click="save"
          >
            Save Video
          </v-btn>
        </v-card-actions>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
import ImageCropper from '@/components/modals/ImageCropper'
import QuillEditor from '@/components/QuillEditor'
import Resource from '@/models/Resource'
import ResourceVideo from '@/models/ResourceVideo'
import ResourceVideoAttachment from '@/models/ResourceVideoAttachment'
import { cloneDeep, each } from 'lodash'

export default {
  components: {
    ImageCropper,
    QuillEditor
  },
  data() {
    return {
      showDialog: false,
      showImageUpload: false,
      form: null,
      cover: null,
      quillKey: 0,
      loading: false
    }
  },
  computed: {
    videoCover() {
      if (this.cover) {
        return this.cover
      } else if (this.form.cover) {
        return this.form.cover.url
      }
      return null
    }
  },
  methods: {
    open(video) {
      this.quillKey = Math.random()
      this.showDialog = true
      this.setForm(video)
      this.imageChanged(video.cover)
    },

    setForm(video) {
      this.cover = null
      this.form = new ResourceVideo(cloneDeep(video))
      requestAnimationFrame(() => {
        this.$refs.form.reset()
      })
    },

    addCover() {
      this.showImageUpload = true
    },

    addAttachment() {
      this.$refs.attachmentPicker.click()
    },

    onAttachmentAdded(e) {
      const form = new FormData()
      const file = e.target.files[0]
      if (file) {
        form.append('file', file)
        this.$api
          .post(
            `/resources/${this.form.resource_id}/videos/${
              this.form.id
            }/attachments`,
            form
          )
          .then(({ data }) => {
            const attachment = new ResourceVideoAttachment(data.data)
            this.$refs.attachmentPicker.value = ''
            this.form.attachments.push(attachment)
            this.$emit('saved', this.form)
          })
      }
    },

    viewAttachment(attachment) {
      window.open(attachment.url, '_blank')
    },

    removeAttachment(attachment, index) {
      const resource = new Resource({ id: this.form.resource_id })
      attachment = new ResourceVideoAttachment(attachment)
      attachment
        .for(resource, this.form)
        .delete()
        .then(() => {
          this.$delete(this.form.attachments, index)
          this.$emit('saved', this.form)
        })
    },

    imageChanged(image) {
      if (image instanceof Blob) {
        const reader = new FileReader()
        reader.readAsDataURL(image)
        reader.onload = e => {
          this.cover = reader.result
          this.form.cover = image
        }
      }
    },

    async save() {
      const isValid = await this.$refs.form.validate()
      let request = null
      if (isValid) {
        this.loading = true
        const form = new FormData()
        if (this.form.id) {
          form.append('_method', 'PUT')
          request = `/resources/${this.form.resource_id}/videos/${this.form.id}`
        } else {
          request = `/resources/${this.form.resource_id}/videos`
        }
        each(this.form, (value, key) => {
          if (key === 'cover' && !(value instanceof Blob)) {
          } else if (value) {
            form.append(key, value)
          }
        })

        this.$api
          .post(request, form)
          .then(({ data }) => {
            this.$toast('Resource video successfully saved!', {
              color: 'green'
            })
            this.$emit('saved', new ResourceVideo(data.data))
            this.showDialog = false
          })
          .catch(({ response }) => {
            if (response.data && response.data.errors) {
              this.$refs.form.setErrors(response.data.errors)
            }
          })
          .then(() => {
            this.loading = false
          })
      }
    }
  }
}
</script>
